"use client";

import Select from "@/component/common/Select";
import { CONTACT_REASONS } from "@/data/contact-data";
import { useContent, useSubmitConsultation } from "@/hooks/useContent";
import type { Bilingual, Locale } from "@/types/home";
import { useLocale } from "next-intl";
import { useState } from "react";

interface ContactContent { reasons?: Bilingual[] }

const field =
  "w-full border border-black/15 bg-transparent p-3 text-sm text-ash outline-none transition-colors focus:border-accent dark:border-white/15 dark:text-white";

/** Contact form with a styled reason select — submits a general lead to the backend. */
export default function ContactForm() {
  const locale = useLocale() as Locale;
  const ar = locale === "ar";
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [reason, setReason] = useState("0");
  const submit = useSubmitConsultation();
  const { data: contact } = useContent<ContactContent>("contact");
  const reasons = contact?.reasons?.length ? contact.reasons : CONTACT_REASONS;

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const reasonLabel = reasons[Number(reason)]?.en ?? "General enquiry";
    submit.mutate({
      name: form.name,
      phone: form.phone,
      email: form.email,
      message: form.message,
      targetType: "general",
      subject: reasonLabel,
    });
  };

  if (submit.isSuccess) {
    return (
      <div className="flex flex-col items-center justify-center border border-black/10 p-12 text-center dark:border-white/10">
        <div className="mb-4 h-2 w-2 bg-accent" />
        <p className="font-semibold text-ash dark:text-white">{ar ? "تم إرسال رسالتك!" : "Message sent!"}</p>
        <p className="mt-2 text-sm text-ash/60 dark:text-white/60">{ar ? "سنعاود التواصل خلال ساعتين." : "We'll be in touch within two hours."}</p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="space-y-4 border border-black/10 p-6 md:p-8 dark:border-white/10">
      <input required placeholder={ar ? "الاسم الكامل" : "Full name"} className={field} value={form.name} onChange={set("name")} />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <input required type="email" placeholder={ar ? "البريد الإلكتروني" : "Email"} className={field} value={form.email} onChange={set("email")} />
        <input required type="tel" placeholder={ar ? "رقم الهاتف" : "Phone"} className={field} value={form.phone} onChange={set("phone")} />
      </div>
      <div>
        <span className="mb-2 block text-[11px] uppercase tracking-widest text-ash/50 dark:text-white/50">{ar ? "سبب التواصل" : "Reason"}</span>
        <Select value={reason} onChange={setReason} options={reasons.map((r, i) => ({ value: String(i), label: r[locale] }))} />
      </div>
      <textarea required rows={5} placeholder={ar ? "رسالتك" : "Your message"} className={field} value={form.message} onChange={set("message")} />
      {submit.isError && (
        <p className="border border-accent/30 bg-accent/5 px-4 py-3 text-[12px] font-semibold text-accent">
          {submit.error instanceof Error ? submit.error.message : (ar ? "تعذّر الإرسال" : "Failed to send")}
        </p>
      )}
      <button type="submit" disabled={submit.isPending} className="w-full bg-accent py-4 text-[12px] font-semibold uppercase tracking-widest text-white transition-colors hover:bg-ash disabled:opacity-60 dark:hover:bg-white dark:hover:text-ash">
        {submit.isPending ? (ar ? "جارٍ الإرسال…" : "Sending…") : ar ? "إرسال الرسالة" : "Send message"}
      </button>
    </form>
  );
}
